import { Text } from "@dropbox/dig-components/typography";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import classNames from "classnames";
import {
  getCalendarSubtitle,
  getVirtualPathSegments,
  shouldDisplayVirtualPath,
} from "../util/resultUtil";
import { DefaultMetadata } from "./DefaultMetadata";
import { FilePathMetadata } from "./FilePathMetadata";
import { MetadataItem } from "./MetadataItem";
import styles from "./TypeaheadResultSubtitle.module.css";

import type { SearchResult } from "@mirage/service-dbx-api";
import type { typeahead } from "@mirage/service-typeahead-search/service/types";

const renderSearchResultContent = (result: SearchResult) => {
  if (result.recordType[".tag"] === "event") {
    return <MetadataItem title={getCalendarSubtitle(result)} />;
  }
  return <DefaultMetadata result={result} />;
};

type TypeaheadResultSubtitleProps = {
  result: typeahead.ScoredResult;
};

export const TypeaheadResultSubtitle = ({
  result,
}: TypeaheadResultSubtitleProps) => {
  const isMobileSize = useIsMobileSize();
  const isVirtualPathEnabled = convertFeatureValueToBool(
    useFeatureFlagValue("dash_2024_11_13_search_results_virtual_path"),
  );

  const renderContent = () => {
    if (
      isVirtualPathEnabled &&
      (result.type === "search-result" || result.type === "recommendation")
    ) {
      const virtualPathSegments = getVirtualPathSegments(result.result);
      if (shouldDisplayVirtualPath(virtualPathSegments)) {
        return (
          <FilePathMetadata
            result={result.result}
            pathSegments={virtualPathSegments}
          />
        );
      }
    }
    switch (result.type) {
      case "calendar-event":
        // Note: CalendarEvent will have the three necessary parameters for displaying
        // subtitle data found in SearchResult: isAllDay, startTime, endTime
        // ideally would scope these util functions to pass in necessary parameters instead of larger data globs
        return <MetadataItem title={getCalendarSubtitle(result.result)} />;
      case "search-result":
        return renderSearchResultContent(result.result);
      case "recommendation":
        return <DefaultMetadata result={result.result} />;
      default:
        return null;
    }
  };

  return (
    <Text size="small" color="faint">
      <div
        className={classNames(styles.container, {
          [styles.isMobile]: isMobileSize,
        })}
      >
        {renderContent()}
      </div>
    </Text>
  );
};
